import { create } from 'zustand';
import { api } from '../services/api';

export interface CalendarEvent {
  id: string;
  family_id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  recurrence_rule: string | null;
  location: string;
  colour: string;
  event_type: string;
  created_by: string;
  assigned_members: string[];
  created_at: string;
  updated_at: string;
  external_source?: 'google' | 'ios' | 'android' | null;
  external_id?: string | null;
  external_calendar_id?: string | null;
  external_synced_at?: string | null;
}

export interface CalendarIntegration {
  id: string;
  provider: 'google' | 'ios' | 'android';
  account_label: string | null;
  external_account_id: string | null;
  selected_calendar_ids: string[];
  last_synced_at: string | null;
  last_sync_status: 'ok' | 'partial' | 'error' | null;
  last_sync_error: string | null;
  last_sync_count: number | null;
  token_expires_at: string | null;
  created_at: string;
}

export interface GoogleCalendarListItem {
  id: string;
  summary: string;
  primary: boolean;
  colour: string | null;
  access_role: string;
}

interface CalendarState {
  events: CalendarEvent[];
  isLoading: boolean;
  error: string | null;
  integrations: CalendarIntegration[];
  googleConfigured: boolean;
  fetchEvents: (start?: string, end?: string) => Promise<void>;
  fetchUpcoming: () => Promise<void>;
  createEvent: (data: Partial<CalendarEvent>) => Promise<CalendarEvent>;
  updateEvent: (eventId: string, data: Partial<CalendarEvent>) => Promise<void>;
  deleteEvent: (eventId: string) => Promise<void>;
  // Integrations
  fetchIntegrations: () => Promise<void>;
  startGoogleConnect: (returnTo?: string) => Promise<string>;
  disconnectIntegration: (id: string, purge?: boolean) => Promise<void>;
  syncGoogle: (id: string) => Promise<{ inserted: number; updated: number; status: string }>;
  listGoogleCalendars: (id: string) => Promise<{ calendars: GoogleCalendarListItem[]; selected: string[] }>;
  setGoogleCalendarSelection: (id: string, calendarIds: string[]) => Promise<void>;
  importNativeEvents: (platform: 'ios' | 'android', events: any[], accountLabel?: string) => Promise<{ inserted: number; updated: number; skipped: number }>;
  // WebSocket handlers
  handleWsMessage: (message: any) => void;
}

export const useCalendarStore = create<CalendarState>((set, get) => ({
  events: [],
  isLoading: false,
  error: null,
  integrations: [],
  googleConfigured: false,

  fetchEvents: async (start?, end?) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (start) params.append('start', start);
      if (end) params.append('end', end);
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/calendar${query}`);
      set({ events: res.data.events, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  fetchUpcoming: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/calendar/upcoming');
      set({ events: res.data.events, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createEvent: async (data) => {
    try {
      const res = await api.post('/calendar', data);
      const newEvent = res.data.event;
      set(state => {
        if (state.events.some(e => e.id === newEvent.id)) return state;
        return { events: [newEvent, ...state.events] };
      });
      return newEvent;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateEvent: async (eventId, data) => {
    try {
      const res = await api.put(`/calendar/${eventId}`, data);
      const updatedEvent = res.data.event;
      set(state => ({
        events: state.events.map(e => e.id === eventId ? updatedEvent : e),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteEvent: async (eventId) => {
    try {
      await api.delete(`/calendar/${eventId}`);
      set(state => ({
        events: state.events.filter(e => e.id !== eventId),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  fetchIntegrations: async () => {
    try {
      const res = await api.get('/calendar-sync/integrations');
      set({
        integrations: res.data.integrations || [],
        googleConfigured: !!res.data.google_configured,
      });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  startGoogleConnect: async (returnTo?: string) => {
    const params = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : '';
    const res = await api.get(`/calendar-sync/google/auth-url${params}`);
    return res.data.url as string;
  },

  disconnectIntegration: async (id, purge = false) => {
    const params = purge ? '?purge=true' : '';
    await api.delete(`/calendar-sync/integrations/${id}${params}`);
    set(state => ({ integrations: state.integrations.filter(i => i.id !== id) }));
    if (purge) {
      // Refetch events since we removed rows server-side
      await get().fetchEvents();
    }
  },

  syncGoogle: async (id) => {
    const res = await api.post(`/calendar-sync/google/${id}/sync`);
    await get().fetchIntegrations();
    await get().fetchEvents();
    return res.data;
  },

  listGoogleCalendars: async (id) => {
    const res = await api.get(`/calendar-sync/google/${id}/calendars`);
    return res.data;
  },

  setGoogleCalendarSelection: async (id, calendarIds) => {
    await api.put(`/calendar-sync/google/${id}/calendars`, { calendar_ids: calendarIds });
    await get().fetchIntegrations();
  },

  importNativeEvents: async (platform, events, accountLabel) => {
    const res = await api.post('/calendar-sync/native/import', {
      platform,
      account_label: accountLabel,
      events,
    });
    await get().fetchIntegrations();
    await get().fetchEvents();
    return res.data;
  },

  handleWsMessage: (message) => {
    switch (message.type) {
      case 'EVENT_CREATED':
        set(state => {
          const exists = state.events.find(e => e.id === message.payload.id);
          if (exists) return state;
          return { events: [message.payload, ...state.events] };
        });
        break;

      case 'EVENT_UPDATED':
        set(state => ({
          events: state.events.map(e =>
            e.id === message.payload.id ? { ...e, ...message.payload } : e
          ),
        }));
        break;

      case 'EVENT_DELETED':
        set(state => ({
          events: state.events.filter(e => e.id !== message.payload.eventId),
        }));
        break;

      case 'CALENDAR_SYNCED':
      case 'CALENDAR_SYNC_PURGED':
        // Another device finished a sync — pull fresh state.
        get().fetchEvents();
        get().fetchIntegrations();
        break;
    }
  },
}));
